/**
 * Log stream panel component - displays real-time container logs.
 *
 * @module components/terminal/LogStreamPanel
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  X,
  Trash2,
  Clock,
  Loader2,
  AlertCircle,
  RefreshCw,
  Search,
  Download,
  Copy,
  Check,
  Maximize2,
  Minimize2,
  ScrollText,
} from 'lucide-react';
import { useLogStream, type LogEntry, type LogStreamConnectionState } from '@/hooks/useLogStream';

/** Log level type for color-coding */
type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'unknown';

/** Stream filter type - 'all' or specific stream */
type StreamFilter = 'all' | 'stdout' | 'stderr' | 'status';

interface LogStreamPanelProps {
  sandboxId: string;
  /** Optional sandbox group ID — routes log stream through group-scoped URL */
  sandboxGroupId?: string;
  onClose: () => void;
}

/** Format timestamp to local time (YYYY-MM-DD HH:MM:SS) */
function formatTimestamp(isoTimestamp: string): string {
  try {
    const d = new Date(isoTimestamp);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch {
    return isoTimestamp;
  }
}

/** Get badge color class for stream type */
function getStreamBadgeColor(stream: LogEntry['stream']): string {
  switch (stream) {
    case 'stderr':
      return 'bg-red-500/20 text-red-400';
    case 'status':
      return 'bg-gray-500/20 text-gray-400';
    case 'stdout':
    default:
      return 'bg-green-500/20 text-green-400';
  }
}

/** Parse log level from message content */
function parseLogLevel(message: string): LogLevel {
  const upperMessage = message.toUpperCase();
  // Check for common log level patterns at start or with brackets
  if (/^\[?ERROR\]?|^ERR\b|\bERROR:/i.test(message)) return 'error';
  if (/^\[?WARN(ING)?\]?|^WRN\b|\bWARN(ING)?:/i.test(message)) return 'warn';
  if (/^\[?INFO\]?|^INF\b|\bINFO:/i.test(message)) return 'info';
  if (/^\[?DEBUG\]?|^DBG\b|\bDEBUG:/i.test(message)) return 'debug';
  // Also check anywhere in the message for bracketed levels
  if (upperMessage.includes('[ERROR]') || upperMessage.includes('ERROR:')) return 'error';
  if (upperMessage.includes('[WARN]') || upperMessage.includes('[WARNING]')) return 'warn';
  if (upperMessage.includes('[INFO]')) return 'info';
  if (upperMessage.includes('[DEBUG]')) return 'debug';
  return 'unknown';
}

/** Get color class based on log level */
function getLogLevelColor(level: LogLevel): string {
  switch (level) {
    case 'error':
      return 'text-red-400';
    case 'warn':
      return 'text-yellow-400';
    case 'info':
      return 'text-blue-400';
    case 'debug':
      return 'text-gray-500';
    default:
      return '';
  }
}

/** Get effective text color (log level takes priority over stream) */
function getEffectiveTextColor(entry: LogEntry): string {
  // stderr always red
  if (entry.stream === 'stderr') return 'text-red-400';
  if (entry.stream === 'status') return 'text-gray-500';

  // For stdout, check log level
  const level = parseLogLevel(entry.message);
  const levelColor = getLogLevelColor(level);
  return levelColor || 'text-gray-200';
}

/** Format elapsed time since start */
function formatElapsedTime(startTime: Date): string {
  const elapsed = Date.now() - startTime.getTime();
  const seconds = Math.floor(elapsed / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/** Highlight search matches in text using pre-compiled regex */
function highlightMatches(text: string, searchRegex: RegExp | null): React.ReactNode {
  if (!searchRegex) return text;

  // Reset lastIndex for global regex
  searchRegex.lastIndex = 0;
  const parts = text.split(searchRegex);

  return parts.map((part, i) => {
    // Reset and test each part
    searchRegex.lastIndex = 0;
    return searchRegex.test(part) ? (
      <mark key={i} className="bg-yellow-500/40 text-yellow-200 rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    );
  });
}

/**
 * Panel for displaying real-time container logs.
 * Features auto-scroll with scroll-lock on user scroll, timestamps toggle, and stream filtering.
 */
export function LogStreamPanel({ sandboxId, sandboxGroupId, onClose }: LogStreamPanelProps) {
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [wordWrap] = useState(true); // Word wrap always enabled
  const [autoScroll, setAutoScroll] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [streamFilter, setStreamFilter] = useState<StreamFilter>('all');
  const [startTime] = useState(() => new Date());
  const [elapsedTime, setElapsedTime] = useState('0s');

  const panelRef = useRef<HTMLDivElement>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const userScrolledRef = useRef(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { connect, disconnect, clearLogs, logs, connectionState, error } = useLogStream({
    sandboxId,
    sandboxGroupId,
    tailLines: 100,
    logFormat: 'json',
    follow: true,
  });

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  // Memoize search regex for performance - only recompute when searchQuery changes
  const searchRegex = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(${escaped})`, 'gi');
  }, [searchQuery]);

  // Filter and search logs
  const filteredLogs = useMemo(() => {
    return logs.filter((entry) => {
      // Stream filter - 'all' shows everything, otherwise match specific stream
      if (streamFilter !== 'all' && entry.stream !== streamFilter) return false;
      // Search filter
      if (searchQuery.trim()) {
        return entry.message.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
  }, [logs, streamFilter, searchQuery]);

  // Update elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(formatElapsedTime(startTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  // Connect on mount
  useEffect(() => {
    // Small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      connect();
    }, 100);
    return () => {
      clearTimeout(timer);
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [filteredLogs, autoScroll]);

  // Detect user scroll to disable auto-scroll
  const handleScroll = useCallback(() => {
    if (!logContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;

    // If user scrolled up, disable auto-scroll
    if (!isAtBottom && !userScrolledRef.current) {
      userScrolledRef.current = true;
      setAutoScroll(false);
    }
    // If user scrolled back to bottom, re-enable auto-scroll
    else if (isAtBottom && userScrolledRef.current) {
      userScrolledRef.current = false;
      setAutoScroll(true);
    }
  }, []);

  const handleClearLogs = useCallback(() => {
    clearLogs();
    userScrolledRef.current = false;
    setAutoScroll(true);
  }, [clearLogs]);

  const toggleAutoScroll = useCallback(() => {
    const newAutoScroll = !autoScroll;
    setAutoScroll(newAutoScroll);
    userScrolledRef.current = !newAutoScroll;
    if (newAutoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [autoScroll]);

  const handleRetry = useCallback(() => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    disconnect();
    retryTimeoutRef.current = setTimeout(() => {
      connect();
      retryTimeoutRef.current = null;
    }, 100);
  }, [connect, disconnect]);

  // Keyboard shortcuts - scoped to panel (only fires when panel has focus)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Ctrl+F to focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape to clear search
      if (e.key === 'Escape') {
        if (searchQuery) {
          e.preventDefault();
          setSearchQuery('');
        }
      }
      // Ctrl+L to clear logs
      if ((e.ctrlKey || e.metaKey) && e.key === 'l') {
        e.preventDefault();
        handleClearLogs();
      }
    },
    [searchQuery, handleClearLogs]
  );

  // Copy log line to clipboard
  const handleCopyLine = useCallback(async (entry: LogEntry) => {
    if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    const text = `${formatTimestamp(entry.timestamp)} [${entry.stream.toUpperCase()}] ${entry.message}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(entry.id);
      copiedTimeoutRef.current = setTimeout(() => {
        setCopiedId(null);
        copiedTimeoutRef.current = null;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Download logs as file
  const handleDownloadLogs = useCallback(() => {
    const content = logs
      .map(
        (entry) =>
          `${formatTimestamp(entry.timestamp)} [${entry.stream.toUpperCase()}] ${entry.message}`
      )
      .join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `container-logs-${sandboxId}-${new Date().toISOString().slice(0, 10)}.log`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, sandboxId]);

  // Set stream filter (single select)
  const setFilter = useCallback((filter: StreamFilter) => {
    setStreamFilter(filter);
  }, []);

  const getStatusIcon = (state: LogStreamConnectionState) => {
    switch (state) {
      case 'connecting':
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-400" />;
      case 'connected':
        return <div className="h-2 w-2 rounded-full bg-green-500" />;
      case 'error':
        return <AlertCircle className="h-3.5 w-3.5 text-red-400" />;
      default:
        return <div className="h-2 w-2 rounded-full bg-gray-500" />;
    }
  };

  const getStatusText = (state: LogStreamConnectionState) => {
    switch (state) {
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return 'Live';
      case 'error':
        return 'Error';
      default:
        return 'Disconnected';
    }
  };

  return (
    <div
      ref={panelRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={`border border-[#2a2a2a] rounded-lg bg-[#0d0d0d] overflow-hidden flex flex-col focus:outline-none focus:ring-1 focus:ring-blue-500/50 ${
        isFullscreen ? 'fixed inset-4 z-50' : ''
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#161616] border-b border-[#2a2a2a] flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <ScrollText className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <span className="text-sm font-medium text-gray-200">LogStream</span>
          </div>
          <div className="flex items-center gap-1.5" role="status" aria-live="polite">
            {getStatusIcon(connectionState)}
            <span className="text-xs text-gray-400">{getStatusText(connectionState)}</span>
          </div>
          {logs.length > 0 && (
            <span className="text-xs text-gray-500" aria-live="polite">
              {filteredLogs.length === logs.length
                ? `${logs.length} ${logs.length === 1 ? 'line' : 'lines'}`
                : `${filteredLogs.length}/${logs.length} lines`}
            </span>
          )}
          {connectionState === 'connected' && (
            <span className="text-xs text-gray-600">• {elapsedTime}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Timestamp toggle */}
          <button
            onClick={() => setShowTimestamps(!showTimestamps)}
            aria-label={showTimestamps ? 'Hide timestamps' : 'Show timestamps'}
            aria-pressed={showTimestamps}
            className={`p-1.5 rounded hover:bg-[#2a2a2a] transition-colors ${
              showTimestamps ? 'text-blue-400' : 'text-gray-500'
            }`}
            title={showTimestamps ? 'Hide timestamps' : 'Show timestamps'}
          >
            <Clock className="h-4 w-4" aria-hidden="true" />
          </button>

          {/* Download logs */}
          <button
            onClick={handleDownloadLogs}
            className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] transition-colors"
            title="Download logs"
            aria-label="Download logs"
            disabled={logs.length === 0}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
          </button>

          {/* Clear logs */}
          <button
            onClick={handleClearLogs}
            className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] transition-colors"
            title="Clear logs (Ctrl+L)"
            aria-label="Clear logs"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>

          {/* Fullscreen toggle */}
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-pressed={isFullscreen}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
            )}
          </button>

          {/* Reconnect button - shows when disconnected or error */}
          {(connectionState === 'disconnected' || connectionState === 'error') && (
            <button
              onClick={handleRetry}
              className="p-1.5 rounded text-yellow-400 hover:text-yellow-300 hover:bg-[#2a2a2a] transition-colors"
              title="Reconnect"
              aria-label="Reconnect to log stream"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
            </button>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 rounded text-gray-400 hover:text-gray-200 hover:bg-[#2a2a2a] transition-colors ml-1"
            title="Close log stream"
            aria-label="Close log stream panel"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Search and filter bar - always visible */}
      <div className="flex items-center gap-3 px-4 py-2 bg-[#1a1a1a] border-b border-[#2a2a2a] flex-shrink-0">
        <div className="relative flex-1 max-w-xs">
          <Search
            className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search logs..."
            aria-label="Search logs"
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#0d0d0d] border border-[#2a2a2a] rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>

        <div
          className="flex items-center gap-1 text-xs"
          role="group"
          aria-label="Filter logs by stream type"
        >
          <span className="text-gray-500 mr-1">Filter:</span>
          <button
            onClick={() => setFilter('all')}
            aria-pressed={streamFilter === 'all'}
            className={`px-2 py-1 rounded transition-colors ${
              streamFilter === 'all' ? 'bg-blue-500/20 text-blue-400' : 'bg-[#2a2a2a] text-gray-500'
            }`}
          >
            ALL
          </button>
          <button
            onClick={() => setFilter('stdout')}
            aria-pressed={streamFilter === 'stdout'}
            className={`px-2 py-1 rounded transition-colors ${
              streamFilter === 'stdout'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-[#2a2a2a] text-gray-500'
            }`}
          >
            STDOUT
          </button>
          <button
            onClick={() => setFilter('stderr')}
            aria-pressed={streamFilter === 'stderr'}
            className={`px-2 py-1 rounded transition-colors ${
              streamFilter === 'stderr'
                ? 'bg-red-500/20 text-red-400'
                : 'bg-[#2a2a2a] text-gray-500'
            }`}
          >
            STDERR
          </button>
          <button
            onClick={() => setFilter('status')}
            aria-pressed={streamFilter === 'status'}
            className={`px-2 py-1 rounded transition-colors ${
              streamFilter === 'status'
                ? 'bg-gray-500/20 text-gray-400'
                : 'bg-[#2a2a2a] text-gray-500'
            }`}
          >
            STATUS
          </button>
        </div>
      </div>

      {/* Log content */}
      <div
        ref={logContainerRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-label="Log stream output"
        className={`overflow-y-auto font-mono text-xs p-3 log-scrollbar ${
          isFullscreen ? 'flex-1' : 'h-[480px]'
        }`}
      >
        {connectionState === 'connecting' && logs.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
            Connecting to log stream...
          </div>
        )}

        {connectionState === 'error' && (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <AlertCircle className="h-6 w-6 text-red-400 mb-2" aria-hidden="true" />
            <span className="text-red-400">{error || 'Connection failed'}</span>
            <button
              onClick={handleRetry}
              className="mt-2 px-3 py-1 text-xs bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {connectionState === 'connected' && logs.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-500">
            Waiting for logs...
          </div>
        )}

        {filteredLogs.length === 0 && logs.length > 0 && (
          <div className="flex items-center justify-center h-full text-gray-500">
            No logs match your search/filter criteria
          </div>
        )}

        {filteredLogs.map((entry, index) => (
          <div
            key={entry.id}
            className={`group flex items-start gap-2 leading-relaxed py-0.5 hover:bg-[#1a1a1a] rounded ${
              index % 2 === 0 ? 'bg-transparent' : 'bg-[#0f0f0f]'
            }`}
          >
            {showTimestamps && (
              <span className="text-gray-600 flex-shrink-0 select-none">
                {formatTimestamp(entry.timestamp)}
              </span>
            )}
            <span
              className={`flex-shrink-0 px-1 py-0.5 rounded text-[10px] font-medium uppercase select-none ${getStreamBadgeColor(entry.stream)}`}
            >
              {entry.stream === 'stdout'
                ? 'STDOUT'
                : entry.stream === 'stderr'
                  ? 'STDERR'
                  : 'STATUS'}
            </span>
            <span
              className={`flex-1 ${wordWrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'} ${getEffectiveTextColor(entry)}`}
            >
              {highlightMatches(entry.message, searchRegex)}
            </span>
            {/* Copy button on hover */}
            <button
              onClick={() => handleCopyLine(entry)}
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#2a2a2a] transition-opacity flex-shrink-0"
              title="Copy line"
              aria-label="Copy log line"
            >
              {copiedId === entry.id ? (
                <Check className="h-3 w-3 text-green-400" aria-hidden="true" />
              ) : (
                <Copy className="h-3 w-3 text-gray-500" aria-hidden="true" />
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Footer - auto-scroll indicator */}
      {!autoScroll && logs.length > 0 && (
        <div className="px-4 py-1.5 bg-[#1a1a1a] border-t border-[#2a2a2a] flex items-center justify-center">
          <button onClick={toggleAutoScroll} className="text-xs text-blue-400 hover:text-blue-300">
            ↓ Auto-scroll paused - Click to resume
          </button>
        </div>
      )}
    </div>
  );
}
